import {
  Button,
  Card,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus, IconRefresh } from '@tabler/icons-react'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { AddCameraHandler } from '@/components/cameras/AddCameraHandler'
import { AddCameraModalWithTabs } from '@/components/cameras/AddCameraModalWithTabs/AddCameraModalWithTabs'
import { DeleteCameraModal } from '@/components/cameras/DeleteCameraModal'
import { ImportCompletionStatus } from '@/components/cameras/ImportCameras/milestone/ImportProgressHandler/ImportProgressHandler'
import { MilestoneImportHandler } from '@/components/cameras/ImportCameras/milestone/MilestoneImportHandler'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetMultipleDeviceDetails } from '@/queries/deviceQueries'
import { CameraStreamWithDeviceId } from '@/types/camera'
import { CrudAction, Resource } from '@/types/permissions'
import { getAugmentedCameras } from '@/utils/device/getAugmentedCameras'
import { InputSourceFormValues } from '../InputSourceSelection/types'
import { SelectableCameraCard } from './SelectableCameraCard/SelectableCameraCard'

type CameraStreamSelectionProps = {
  deviceIds: string[]
  form: UseFormReturnType<InputSourceFormValues>
}

export const CameraStreamSelection = ({
  deviceIds,
  form
}: CameraStreamSelectionProps) => {
  const selectedCameras = form.getValues().selectedCameras

  const [
    isAddCameraModalOpen,
    { open: openAddCameraModal, close: closeAddCameraModal }
  ] = useDisclosure(false)

  const [cameraToRemove, setCameraToRemove] =
    useState<CameraStreamWithDeviceId | null>(null)

  const [canCreateCamera, canDeleteCamera] = useHasPermissions(
    Resource.Cameras,
    [CrudAction.Create, CrudAction.Delete]
  )

  const { data, isError, isFetching, refetch } =
    useGetMultipleDeviceDetails(deviceIds)

  const devices = useMemo(() => data || [], [data])

  const camerasGroupedByDevice = useMemo(
    () =>
      devices.map((device) => {
        return {
          device,
          cameras: getAugmentedCameras(device).sort((a, b) =>
            a.camera_name.localeCompare(b.camera_name, undefined, {
              numeric: true
            })
          )
        }
      }),
    [devices]
  )

  const isCameraSelected = (cameraId: string) => {
    return selectedCameras.some((camera) => camera.camera_id === cameraId)
  }

  const toggleCameraSelection = (camera: CameraStreamWithDeviceId) => {
    const updated = selectedCameras.some(
      (c) => c.camera_id === camera.camera_id
    )
      ? selectedCameras.filter((c) => c.camera_id !== camera.camera_id)
      : [...selectedCameras, camera]

    form.setFieldValue('selectedCameras', updated)
  }

  const handleCameraCreated = useCallback(() => {
    void refetch()
    closeAddCameraModal()
  }, [refetch, closeAddCameraModal])

  const handleCameraRemoved = () => {
    if (cameraToRemove) {
      form.setFieldValue(
        'selectedCameras',
        selectedCameras.filter((c) => c.camera_id !== cameraToRemove.camera_id)
      )
    }

    setCameraToRemove(null)
    void refetch()
  }

  const handleImportCompleted = useCallback(
    (status: ImportCompletionStatus) => {
      if (status === 'success') {
        closeAddCameraModal()
      }
      void refetch()
    },
    [refetch, closeAddCameraModal]
  )

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="devices.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Card withBorder>
        <Card.Section py="md" inheritPadding withBorder>
          <Group>
            {canCreateCamera && (
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={openAddCameraModal}
              >
                <FormattedMessage id="cameras.add" />
              </Button>
            )}

            <Button
              variant="light"
              rightSection={<IconRefresh size={16} />}
              onClick={() => void refetch()}
            >
              <FormattedMessage id="refresh" />
            </Button>
          </Group>
        </Card.Section>

        <Card.Section p="xl" bg="gray.0">
          <Stack gap="lg">
            {form.errors.selectedCameras && (
              <PlatformAlert variant="error">
                {form.errors.selectedCameras}
              </PlatformAlert>
            )}
            <Stack gap={60}>
              {camerasGroupedByDevice.map((group) => (
                <Stack key={group.device.id} gap="sm">
                  <Title order={4} fz={16}>
                    {group.device.label} ({group.cameras.length})
                  </Title>

                  {group.cameras.length === 0 && !isFetching && (
                    <Text size="sm" fs="italic">
                      <FormattedMessage id="cameras.device.noCameras" />
                    </Text>
                  )}

                  <SimpleGrid
                    cols={{ base: 1, sm: 2, lg: 3, xl: 5 }}
                    spacing="xl"
                  >
                    {group.cameras.map((camera) => (
                      <SelectableCameraCard
                        key={camera.camera_id}
                        name={camera.camera_name}
                        thumbnailUrl={camera.thumbnail}
                        source={camera.source}
                        streamType={camera.stream_type}
                        isActive={camera.connected}
                        isSelected={isCameraSelected(camera.camera_id)}
                        disabled={
                          !camera.connected &&
                          !isCameraSelected(camera.camera_id)
                        }
                        showRemoveIcon={canDeleteCamera}
                        onClick={() => toggleCameraSelection(camera)}
                        onRemoveClick={() => setCameraToRemove(camera)}
                      />
                    ))}
                  </SimpleGrid>
                </Stack>
              ))}
            </Stack>

            {isFetching && (
              <Center h={120}>
                <Loader size={32} />
              </Center>
            )}
          </Stack>
        </Card.Section>
      </Card>

      <AddCameraModalWithTabs
        opened={isAddCameraModalOpen}
        addTab={
          <AddCameraHandler
            devices={devices}
            onCancel={closeAddCameraModal}
            onCameraCreated={handleCameraCreated}
          />
        }
        importTab={
          <MilestoneImportHandler
            devices={devices}
            onCompletion={handleImportCompleted}
            onClose={closeAddCameraModal}
          />
        }
        onClose={closeAddCameraModal}
      />

      <DeleteCameraModal
        opened={!!cameraToRemove}
        camera={cameraToRemove || undefined}
        onClose={() => setCameraToRemove(null)}
        onDelete={handleCameraRemoved}
      />
    </>
  )
}
